import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, FileText, Clock, CheckCircle, Eye, Trash2, X, ChevronDown, Loader2, ClipboardList, Printer, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const statusColors = {
  'New': 'bg-blue-100 text-blue-700',
  'Reviewed': 'bg-amber-100 text-amber-700',
  'Contacted': 'bg-green-100 text-green-700',
};

const AdminNutritionApps = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/nutrition-applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/nutrition-applications/${appId}/status`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setApplications(applications.map(a => a.id === appId ? { ...a, status: newStatus } : a));
        if (selectedApp?.id === appId) setSelectedApp({ ...selectedApp, status: newStatus });
        toast.success(`Status updated to "${newStatus}"`);
      } else { toast.error('Failed to update status'); }
    } catch (error) { toast.error('Failed to update status'); }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/nutrition-applications/${appId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setApplications(applications.filter(a => a.id !== appId));
        setSelectedApp(null);
        toast.success('Application deleted');
      } else { toast.error('Failed to delete'); }
    } catch (error) { toast.error('Failed to delete'); }
  };

  const viewApplication = async (appId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/nutrition-applications/${appId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedApp(data);
      }
    } catch (error) { toast.error('Failed to load application'); }
  };

  const filtered = applications.filter(a => {
    const matchesSearch = !searchTerm || 
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-sage-500" />
      </div>
    );
  }

  // Detail view
  if (selectedApp) {
    return <ApplicationDetail app={selectedApp} onBack={() => setSelectedApp(null)} onUpdateStatus={updateStatus} onDelete={deleteApplication} />;
  }

  return (
    <div data-testid="admin-nutrition-apps">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-charcoal">Nutrition Applications</h1>
          <p className="text-charcoal/60 text-sm mt-1">{applications.length} application{applications.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 h-11 bg-cream-50 border-border/50 rounded-xl"
            data-testid="nutrition-apps-search"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'New', 'Reviewed', 'Contacted'].map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              className={`rounded-lg text-xs ${statusFilter === s ? 'bg-charcoal text-white' : ''}`}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s.toLowerCase()}`}
            >
              {s === 'all' ? 'All' : s}
            </Button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="bg-cream-50 border-0 rounded-2xl">
          <CardContent className="py-16 text-center">
            <ClipboardList className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
            <h3 className="font-serif text-xl text-charcoal mb-2">No Applications</h3>
            <p className="text-charcoal/60 text-sm">
              {searchTerm || statusFilter !== 'all' ? 'No applications match your filters.' : 'No nutrition applications have been submitted yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((app, index) => (
            <motion.div
              key={app.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-200 rounded-xl cursor-pointer"
                onClick={() => viewApplication(app.id)}
                data-testid={`nutrition-app-${app.id}`}
              >
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-sage-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-charcoal truncate">{app.name || 'No name'}</p>
                      <p className="text-charcoal/50 text-sm truncate">{app.email || 'No email'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[app.status] || 'bg-gray-100 text-gray-600'}`}>
                      {app.status}
                    </span>
                    <span className="text-charcoal/40 text-xs hidden sm:block">
                      {app.submitted_date ? new Date(app.submitted_date).toLocaleDateString() : ''}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Section renderer for the detail view
const Section = ({ title, children }) => (
  <div className="mb-8">
    <h3 className="font-serif text-lg font-medium text-charcoal mb-4 pb-2 border-b border-border/40">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs font-medium text-charcoal/50 uppercase tracking-wider">{label}</span>
      <p className="text-charcoal mt-1 whitespace-pre-wrap">{value}</p>
    </div>
  );
};

const YesNoField = ({ label, value, detail }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-1">
      <span className={`text-xs font-bold px-2 py-0.5 rounded ${value === 'Yes' ? 'bg-amber-100 text-amber-700' : value === 'Occasionally' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
        {value === 'Occasionally' ? 'OCC' : value}
      </span>
      <div className="min-w-0">
        <span className="text-charcoal text-sm">{label}</span>
        {detail && <p className="text-charcoal/60 text-sm mt-0.5">{detail}</p>}
      </div>
    </div>
  );
};

const ApplicationDetail = ({ app, onBack, onUpdateStatus, onDelete }) => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const digestiveQuestions = [
    ['belching', 'Belching', 'belching_detail'],
    ['heartburn', 'Heartburn', 'heartburn_detail'],
    ['acid_reflux', 'Acid reflux or night time reflux', 'acid_reflux_detail'],
    ['antacids', 'Use of antacids', 'antacids_detail'],
    ['coated_tongue', 'Coated tongue'],
    ['stomach_upsets', 'Frequent stomach upsets', 'stomach_upsets_detail'],
    ['greasy_foods', 'Stomach upset by greasy foods'],
    ['skip_breakfast', 'Feel like skipping breakfast'],
    ['bloating', 'Bloating', 'bloating_detail'],
    ['abdominal_pains', 'Abdominal pains or cramps', 'abdominal_pains_detail'],
    ['diarrhoea', 'Diarrhoea'],
    ['alternating_constipation', 'Alternating constipation and diarrhoea'],
    ['bowel_frequency', 'Bowel movement frequency', 'bowel_frequency_detail'],
    ['black_stools', 'Black or tarry stools'],
    ['undigested_food', 'Undigested food in stools'],
    ['light_stools', 'Light clay coloured stools'],
    ['greasy_stools', 'Greasy or shiny stools'],
    ['anus_itching', 'Anus itching'],
    ['parasites', 'Known history of parasites, worms or bacterial infestations'],
    ['blood_in_stool', 'Blood in stool', 'blood_in_stool_detail'],
    ['mucous_in_stool', 'Mucous in stool', 'mucous_in_stool_detail'],
  ];

  const liverQuestions = [
    ['nausea', 'Frequent nausea'],
    ['motion_sickness', 'Tendency to motion sickness'],
    ['bitter_taste', 'Bitter taste in mouth especially after meals'],
    ['caffeine_reaction', 'Strong reaction to caffeine', 'caffeine_reaction_detail'],
    ['haemorrhoids', 'Haemorrhoids'],
  ];

  const endocrineQuestions = [
    ['difficulty_sleeping', 'Difficulty sleeping'],
    ['hard_mornings', 'Hard to get going in the morning'],
    ['frequent_fatigue', 'Frequent fatigue'],
    ['drowsiness', 'Frequent drowsiness during the day', 'drowsiness_detail'],
    ['dizziness_hungry', 'Dizziness or irritability if you don\'t eat often'],
    ['sweating', 'Frequent sweating'],
    ['excessive_thirst', 'Excessive thirst'],
    ['reduced_energy', 'Reduced energy'],
    ['depression', 'Tendency to depression or social isolation'],
    ['temp_intolerance', 'Intolerance to cold or heat'],
    ['cold_extremities', 'Cold hands and feet'],
    ['headaches', 'Frequent headaches or migraines'],
    ['anxiety', 'Nervousness or anxiety'],
    ['teeth_grinding', 'Teeth-grinding'],
  ];

  const immuneQuestions = [
    ['runny_nose', 'Runny or drippy nose', 'runny_nose_detail'],
    ['frequent_infections', 'Frequent infections/colds and flu'],
    ['antibiotic_use', 'Frequent antibiotic use', 'antibiotic_use_detail'],
    ['cystitis', 'Frequent cystitis', 'cystitis_detail'],
    ['thrush', 'Frequent thrush', 'thrush_detail'],
    ['chronic_viral', 'History of chronic viral conditions', 'chronic_viral_detail'],
    ['inflammatory', 'Inflammatory conditions', 'inflammatory_detail'],
  ];

  const familyConditions = [
    'Heart Disease', 'High Blood Pressure', 'High Cholesterol', 'Osteoarthritis',
    'Rheumatoid Arthritis', 'Thyroid Problems', 'Osteoporosis', 'Allergies',
    'Obesity', 'Asthma', 'Depression', 'Alcoholism', 'Cancer',
    'Type 1 Diabetes', 'Type 2 Diabetes (late onset)'
  ];

  const renderProfileSection = (title, questions, profileKey) => {
    const profile = app[profileKey];
    if (!profile) return null;
    const hasAnswers = questions.some(q => profile[q[0]]);
    if (!hasAnswers) return null;
    return (
      <Section title={title}>
        {questions.map(([key, label, detailKey]) => {
          const val = profile[key];
          if (!val) return null;
          return <YesNoField key={key} label={label} value={val} detail={detailKey ? profile[detailKey] : undefined} />;
        })}
      </Section>
    );
  };

  const handlePrint = () => {
    const pw = window.open('', '_blank');
    if (!pw) { toast.error('Please allow popups to print'); return; }

    const esc = (v) => v ? String(v).replace(/</g, '&lt;') : '';

    const fieldSections = [
      { title: 'General Information', fields: [
        ['Name', app.name], ['Email', app.email], ['Mobile', app.mobile],
        ['Address', app.address], ['Date of Birth', app.date_of_birth],
        ['Occupation', app.occupation], ['Height', app.height], ['Weight', app.weight],
        ['Smoker', app.smoker], ['Last Blood Test', app.last_blood_test],
      ]},
      { title: 'Health Goals & Background', fields: [
        ['Reason for consultation / Health concerns', app.health_concerns],
        ['Recent stressful events (last 10 years)', app.stressful_events],
        ['Colonoscopy or scope tests', app.colonoscopy_results],
        ['Seen a Nutritionist before', app.seen_nutritionist],
      ]},
      { title: 'Medication & Supplements', fields: [
        ['Current Medication', app.current_medications],
        ['Past Medication', app.past_medications],
        ['Health Supplements', app.supplements],
      ]},
    ];
    if (app.medical_history) {
      fieldSections.push({ title: 'Medical History', fields: [['Illnesses, operations, and related medication', app.medical_history]] });
    }
    fieldSections.push(
      { title: 'Past Lifestyle', fields: [
        ['Were you breastfed?', app.breastfed],
        ['Childhood vaccinations', app.childhood_vaccinations],
        ['Happy, active child?', app.happy_active_child],
        ['Childhood trauma or adverse events', app.childhood_trauma],
      ]},
      { title: 'Exercise', fields: [
        ['How many times a week', app.exercise_frequency],
        ['Type of exercise', app.exercise_type],
        ['Reason for exercising (or not)', app.exercise_reason],
      ]},
      { title: 'Food Diary', fields: [
        ['Breakfast', app.breakfast], ['Breakfast time', app.breakfast_time],
        ['Lunch', app.lunch], ['Dinner', app.dinner], ['Dinner time', app.dinner_time],
        ['Snacks', app.snacks], ['Snack times', app.snack_times],
        ['Other regular foods or drinks', app.other_foods],
        ['Additional information', app.additional_info],
      ]},
    );

    const renderFields = (fields) => fields
      .filter(([, v]) => v)
      .map(([l, v]) => `<div style="margin-bottom:10px;"><span style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-bottom:2px;">${esc(l)}</span><span style="font-size:14px;color:#2d2d2d;white-space:pre-wrap;">${esc(v)}</span></div>`)
      .join('');

    const sectionStyle = 'margin-bottom:28px;page-break-inside:avoid;';
    const headingStyle = 'font-size:15px;font-weight:600;color:#2d2d2d;border-bottom:2px solid #7a8f6d;padding-bottom:5px;margin-bottom:14px;';

    let bodyHtml = fieldSections.map(s =>
      `<div style="${sectionStyle}"><h2 style="${headingStyle}">${esc(s.title)}</h2>${renderFields(s.fields)}</div>`
    ).join('');

    // Family History table
    if (app.family_history && Object.keys(app.family_history).length > 0) {
      const conditions = ['Heart Disease','High Blood Pressure','High Cholesterol','Osteoarthritis','Rheumatoid Arthritis','Thyroid Problems','Osteoporosis','Allergies','Obesity','Asthma','Depression','Alcoholism','Cancer','Type 1 Diabetes','Type 2 Diabetes (late onset)'];
      const members = ['grandparents','parents','siblings','cousins','offspring'];
      let rows = '';
      conditions.forEach(cond => {
        const key = cond.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const row = app.family_history[key];
        if (!row || !Object.values(row).some(v => v)) return;
        rows += `<tr><td style="padding:4px 8px 4px 0;border-bottom:1px solid #eee;">${esc(cond)}</td>${members.map(m => `<td style="padding:4px 6px;text-align:center;border-bottom:1px solid #eee;">${row[m] ? esc(row[m]) : '—'}</td>`).join('')}</tr>`;
      });
      if (rows) {
        bodyHtml += `<div style="${sectionStyle}"><h2 style="${headingStyle}">Family History</h2><table style="width:100%;border-collapse:collapse;font-size:13px;"><thead><tr style="border-bottom:2px solid #ddd;"><th style="text-align:left;padding:4px 8px 4px 0;">Condition</th>${members.map(m => `<th style="text-align:center;padding:4px 6px;text-transform:capitalize;">${m}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table></div>`;
      }
    }

    // Profile sections (Digestive, Liver, Endocrine, Immune)
    const profileConfigs = [
      { title: 'Digestive Tract Profile', key: 'digestive_profile', questions: digestiveQuestions },
      { title: 'Liver Profile', key: 'liver_profile', questions: liverQuestions },
      { title: 'Endocrine (Hormonal) System', key: 'endocrine_profile', questions: endocrineQuestions },
      { title: 'Immune Profile', key: 'immune_profile', questions: immuneQuestions },
    ];
    profileConfigs.forEach(({ title, key, questions }) => {
      const profile = app[key];
      if (!profile) return;
      const items = questions.filter(q => profile[q[0]]).map(([k, label, dk]) => {
        const val = profile[k];
        const detail = dk ? profile[dk] : '';
        return `<div style="display:flex;align-items:baseline;gap:8px;margin-bottom:6px;"><span style="font-size:11px;font-weight:700;padding:2px 6px;border-radius:3px;background:${val === 'Yes' ? '#fef3c7' : val === 'Occasionally' ? '#dbeafe' : '#f3f4f6'};color:${val === 'Yes' ? '#92400e' : val === 'Occasionally' ? '#1e40af' : '#6b7280'}">${val === 'Occasionally' ? 'OCC' : esc(val)}</span><span style="font-size:13px;color:#2d2d2d;">${esc(label)}${detail ? ' — ' + esc(detail) : ''}</span></div>`;
      });
      if (items.length) {
        bodyHtml += `<div style="${sectionStyle}"><h2 style="${headingStyle}">${esc(title)}</h2>${items.join('')}</div>`;
      }
    });

    const date = app.submitted_date ? new Date(app.submitted_date).toLocaleDateString('en-IE') : 'N/A';
    pw.document.write(`<!DOCTYPE html><html><head><title>Application - ${esc(app.name) || 'N/A'}</title>
<style>
  body { font-family: Georgia, serif; max-width: 720px; margin: 40px auto; padding: 0 24px; color: #2d2d2d; }
  @media print { body { margin: 16px; } }
  @page { margin: 1.5cm; }
</style></head><body>
<h1 style="font-size:22px;margin-bottom:4px;">Nutritional Therapy Application</h1>
<p style="color:#666;font-size:13px;margin-bottom:28px;">Submitted: ${date} &nbsp;&bull;&nbsp; Status: ${esc(app.status) || 'New'}</p>
${bodyHtml}
<script>window.print();<\/script></body></html>`);
    pw.document.close();
  };

  const handleDownloadWord = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/nutrition-applications/${app.id}/docx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) { toast.error('Failed to generate document'); return; }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Nutrition_Application_${(app.name || 'application').replace(/\s+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  return (
    <div data-testid="nutrition-app-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <Button variant="ghost" onClick={onBack} className="gap-2 text-charcoal/60 hover:text-charcoal" data-testid="app-detail-back">
          <X className="w-4 h-4" /> Back to list
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2 rounded-lg" data-testid="app-print-btn">
            <Printer className="w-4 h-4" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadWord} className="gap-2 rounded-lg" data-testid="app-download-word-btn">
            <Download className="w-4 h-4" /> Download Word
          </Button>
          {['New', 'Reviewed', 'Contacted'].map(s => (
            <Button
              key={s}
              variant={app.status === s ? 'default' : 'outline'}
              size="sm"
              className={`rounded-lg text-xs ${app.status === s ? 'bg-charcoal text-white' : ''}`}
              onClick={() => onUpdateStatus(app.id, s)}
              data-testid={`status-${s.toLowerCase()}`}
            >
              {s}
            </Button>
          ))}
          <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50 rounded-lg ml-2"
            onClick={() => onDelete(app.id)} data-testid="app-delete-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Name + meta */}
      <div className="mb-8">
        <h2 className="font-serif text-2xl text-charcoal">{app.name || 'No name'}</h2>
        <div className="flex items-center gap-4 mt-2 text-sm text-charcoal/60">
          {app.email && <span>{app.email}</span>}
          {app.mobile && <span>{app.mobile}</span>}
          {app.submitted_date && <span>{new Date(app.submitted_date).toLocaleDateString()}</span>}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-soft p-6 md:p-8 space-y-2">
        {/* General */}
        <Section title="General Information">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={app.name} />
            <Field label="Email" value={app.email} />
            <Field label="Mobile" value={app.mobile} />
            <Field label="Address" value={app.address} />
            <Field label="Date of Birth" value={app.date_of_birth} />
            <Field label="Occupation" value={app.occupation} />
            <Field label="Height" value={app.height} />
            <Field label="Weight" value={app.weight} />
            <Field label="Do you smoke" value={app.smoker} />
            <Field label="Last blood test" value={app.last_blood_test} />
          </div>
        </Section>

        {/* Health Goals */}
        <Section title="Health Goals & Background">
          <Field label="Reason for consultation / Health concerns" value={app.health_concerns} />
          <Field label="Recent stressful events (in last 10 years)" value={app.stressful_events} />
          <Field label="Any colonoscopy or scope tests done recently" value={app.colonoscopy_results} />
          <Field label="Have you seen a Nutritionist before" value={app.seen_nutritionist} />
        </Section>

        {/* Medication */}
        <Section title="Medication & Supplements">
          <Field label="Current Medication" value={app.current_medications} />
          <Field label="Past Medication" value={app.past_medications} />
          <Field label="Health Supplements" value={app.supplements} />
        </Section>

        {/* Medical History */}
        {app.medical_history && (
          <Section title="Medical History">
            <Field label="Illnesses, operations, and related medication" value={app.medical_history} />
          </Section>
        )}

        {/* Family History */}
        {app.family_history && Object.keys(app.family_history).length > 0 && (
          <Section title="Family History">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/40">
                    <th className="text-left py-2 pr-4 font-medium text-charcoal/60">Condition</th>
                    <th className="py-2 px-2 font-medium text-charcoal/60 text-center">Grand-parents</th>
                    <th className="py-2 px-2 font-medium text-charcoal/60 text-center">Parents</th>
                    <th className="py-2 px-2 font-medium text-charcoal/60 text-center">Siblings</th>
                    <th className="py-2 px-2 font-medium text-charcoal/60 text-center">Cousins</th>
                    <th className="py-2 px-2 font-medium text-charcoal/60 text-center">Offspring</th>
                  </tr>
                </thead>
                <tbody>
                  {familyConditions.map(cond => {
                    const key = cond.toLowerCase().replace(/[^a-z0-9]/g, '_');
                    const row = app.family_history[key];
                    if (!row || !Object.values(row).some(v => v)) return null;
                    return (
                      <tr key={key} className="border-b border-border/20">
                        <td className="py-2 pr-4 text-charcoal">{cond}</td>
                        {['grandparents', 'parents', 'siblings', 'cousins', 'offspring'].map(member => (
                          <td key={member} className="py-2 px-2 text-center">
                            {row[member] ? <span className="text-sage-600 font-semibold">{row[member]}</span> : <span className="text-charcoal/20">—</span>}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Past Lifestyle */}
        <Section title="Past Lifestyle">
          <Field label="Were you breastfed?" value={app.breastfed} />
          <Field label="Childhood vaccinations" value={app.childhood_vaccinations} />
          <Field label="Happy, active child?" value={app.happy_active_child} />
          <Field label="Childhood trauma or adverse events" value={app.childhood_trauma} />
        </Section>

        {/* Digestive Tract */}
        {renderProfileSection('Digestive Tract Profile', digestiveQuestions, 'digestive_profile')}

        {/* Liver */}
        {renderProfileSection('Liver Profile', liverQuestions, 'liver_profile')}

        {/* Endocrine */}
        {renderProfileSection('Endocrine (Hormonal) System', endocrineQuestions, 'endocrine_profile')}

        {/* Immune */}
        {renderProfileSection('Immune Profile', immuneQuestions, 'immune_profile')}

        {/* Exercise */}
        <Section title="Exercise">
          <Field label="How many times a week do you exercise?" value={app.exercise_frequency} />
          <Field label="What type of exercise?" value={app.exercise_type} />
          <Field label="Why do you exercise? (Or why not?)" value={app.exercise_reason} />
        </Section>

        {/* Food Diary */}
        <Section title="Food Diary">
          <Field label="Breakfast" value={app.breakfast} />
          <Field label="Typical breakfast time" value={app.breakfast_time} />
          <Field label="Lunch" value={app.lunch} />
          <Field label="Dinner" value={app.dinner} />
          <Field label="Typical dinner time" value={app.dinner_time} />
          <Field label="Snacks" value={app.snacks} />
          <Field label="Snack times" value={app.snack_times} />
          <Field label="Other regular foods or drinks" value={app.other_foods} />
          <Field label="Additional information" value={app.additional_info} />
        </Section>
      </div>
    </div>
  );
};

export default AdminNutritionApps;
